import {DestroyRef, inject, Injectable} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {distinctUntilChanged, filter} from 'rxjs/operators';
import {PortalMessageBroker} from './portal-message-broker';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';


export enum WellKnownShellProperties {
  APP_MODE_NORMAL = 'shell:app-mode:normal',
  APP_MODE_POPUP = 'shell:app-mode:popup',
  APP_MODE_WIDGET = 'shell:app-mode:widget',
  EMBEDDED_EMBEDDED = 'shell:embedded:embedded',
  EMBEDDED_EMBEDDING = 'shell:embedded:embedding',
  SHELL_MODE = 'shell:shell:mode',
}

export enum WellKnownJustFarmingProperties {
  ANALYTICS = 'vendor:de.just-farming:analytics',
  ERROR_REPORTING = 'vendor:de.just-farming:errorReporting',
}

export const SUPPORTED_MODES = ['default', 'gewerbeportal'] as const;
export type Mode = typeof SUPPORTED_MODES[number];

export type WellKnownProperties = WellKnownShellProperties | WellKnownJustFarmingProperties;
export type PropertyKey = string | WellKnownProperties;

export type Property = boolean | string | number | undefined;
export type ArrayProperty = Property[];
export type ContextInfoProperty = Property | ArrayProperty;

export interface ContextInfo {
  properties: Map<PropertyKey, ContextInfoProperty>;
}


@Injectable({
  providedIn: 'root'
})
export class ContextInfoService {

  private _destroyRef = inject(DestroyRef);
  private _logger = inject(NGXLogger);
  private _portalMessageBroker = inject(PortalMessageBroker);

  private readonly _contextInfo$ = new BehaviorSubject<ContextInfo>({
    properties: new Map(),
  });

  private readonly _shellMode$ = new ReplaySubject<Mode | undefined>(1);
  private shellModeOverride: Mode | null = null;

  constructor() {
    this.checkQueryParameterOverride();

    this.initializeShellMode();

    this._portalMessageBroker.registerContextInfoCallback(contextInfo => {
      this._contextInfo$.next(contextInfo);

      if (!this.shellModeOverride) {
        const shellModeValue = contextInfo.properties.get(WellKnownShellProperties.SHELL_MODE);
        this._shellMode$.next(this.mapToMode(shellModeValue));
      }

      this._logger.debug('ContextInfoService: new context info', this._contextInfo$.value);
    });

    this._destroyRef.onDestroy(() => {
      this._shellMode$.complete();
    });
  }

  get contextInfo(): ContextInfo {
    return this._contextInfo$.value;
  }

  get contextInfo$(): Observable<ContextInfo> {
    return this._contextInfo$.asObservable().pipe(
      takeUntilDestroyed(this._destroyRef),
    );
  }

  get shellMode$(): Observable<Mode> {
    return this._shellMode$.asObservable()
      .pipe(
        filter((mode): mode is Mode => mode !== undefined),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
      );
  }

  /**
   * Legt den Wert einer geteilten Eigenschaft fest.
   *
   * Steht anderen Applications über das ContextInfo Objekt zur Verfügung.
   *
   * @param key eindeutiger Identifikator
   * @param value Wert zum angegebenen Schlüssel
   */
  public submitProperty(key: string, value: ContextInfoProperty): void {
    if (this._portalMessageBroker.isRunningInPortal()) {
      this._portalMessageBroker.submitContextInfoProperty(key, value);
    }
  }

  private async initializeShellMode(): Promise<void> {
    if (this.shellModeOverride) {
      this._shellMode$.next(this.shellModeOverride);
      return;
    }

    this._portalMessageBroker.runningInPortal$.subscribe(runningInPortal => {
      if (!runningInPortal) {
        this._shellMode$.next('default');
        this._logger.debug('ContextInfoService: Not running in portal, using default mode');
      }
    });
  }

  private checkQueryParameterOverride(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const shellModeParam = urlParams.get('shellMode');

    if (shellModeParam && SUPPORTED_MODES.includes(shellModeParam as Mode)) {
      this.shellModeOverride = shellModeParam as Mode;
      this._logger.info(`ContextInfoService: Using shellMode override from query parameter: ${shellModeParam}`);
    }
  }

  private mapToMode(value: any) {
    if (typeof value === 'string' && SUPPORTED_MODES.includes(value as Mode)) {
      return value as Mode;
    }
    this._logger.warn(`ContextInfoService: Unsupported mode value: ${value}, falling back to 'default'`);
    return undefined;
  }

}
